import Chart from 'chart.js';
import moment from 'jalali-moment';
import { LogData } from '../components/interfaces';
import { arrayFill } from './easy';
import { gapForDay, gapForMonth } from './graph-gap';

Chart.defaults.global.defaultFontFamily = 'IRANSans';

export interface ChartOption {
  id: string;
  times: string[];
  datasets: any[];
  type?: string; // Default: 'line'
  unit?: string; // Default: ''
}

export interface ChartUpdate {
  times: string[];
  datasets: any[];
  unit?: string;
}

export interface Dataset {
  data: number[];
  label?: string; // Default: ''
  borderWidth?: number; // Default: 2
  lineTension?: number; // Default: 0
  pointRadius?: number; // Default: 0
}

export interface DatasetConfig {
  data: number[];
  max: number;
  min: number;
  maxPhone: number;
  minPhone: number;
  disconnects: LogData[];
}

export interface ExtractTimesOverload {
  (logs: LogData[], format: string): any[];
  (logs: LogData[], isMonth: boolean): any[];
}

export const SHARED_DATASET_CONFIG = {
  borderWidth: 2,
  lineTension: 0,
  pointRadius: 0,
};

export const GRAPH_COLORS = {
  data: { background: 'rgba(75, 192, 192, 0.2)', point: 'rgba(75, 192, 192, 1)' },
  max: { background: 'rgba(192, 57, 43, 0.0)', point: 'rgba(192, 57, 43, 1.0)' },
  maxPhone: { background: 'rgba(231, 76, 60, 0.0)', point: 'rgba(231, 76, 60, 1.0)' },
  min: { background: 'rgba(41, 128, 185, 0.0)', point: 'rgba(41, 128, 185, 1.0)' },
  minPhone: { background: 'rgba(52, 152, 219, 0.0)', point: 'rgba(52, 152, 219, 1.0)' },
  disconnect: { background: 'rgba(243, 156, 18, 0.0)', point: 'rgba(243, 156, 18, 1.0)' },
}

export const create = (options: ChartOption) => {
  return new Chart(options.id, {
    type: options.type ?? 'line',
    data: { labels: options.times, datasets: options.datasets as any },
    options: chartOption(options.unit ?? '')
  });
}

export const update = (chart: Chart, options: ChartUpdate) => {
  chart.data = { labels: options.times, datasets: options.datasets as any };
  chart.options = chartOption(options.unit ?? '');
  chart.update();
}

export const makeDatasets = (device: any, options: DatasetConfig) => {
  const pointsLength = options.data.length;
  return [
    {
      label: `${device._id} Logs`,
      data: options.data,
      backgroundColor: [GRAPH_COLORS.data.background],
      borderColor: GRAPH_COLORS.data.point,
      borderWidth: 1
    },
    {
      label: `حداکثر مجاز`,
      data: arrayFill(pointsLength, options.max),
      backgroundColor: [GRAPH_COLORS.max.background],
      borderColor: GRAPH_COLORS.max.point,
      ...SHARED_DATASET_CONFIG
    },
    {
      label: `حداکثر مجاز آلارم تلفنی`,
      data: arrayFill(pointsLength, options.maxPhone),
      backgroundColor: [GRAPH_COLORS.maxPhone.background],
      borderColor: GRAPH_COLORS.maxPhone.point,
      ...SHARED_DATASET_CONFIG
    },
    {
      label: `حداقل مجاز`,
      data: arrayFill(pointsLength, options.min),
      backgroundColor: GRAPH_COLORS.min.background,
      borderColor: GRAPH_COLORS.min.point,
      ...SHARED_DATASET_CONFIG
    },
    {
      label: `حداقل مجاز آلارم تلفنی`,
      data: arrayFill(pointsLength, options.minPhone),
      backgroundColor: [GRAPH_COLORS.minPhone.background],
      borderColor: GRAPH_COLORS.minPhone.point,
      ...SHARED_DATASET_CONFIG
    },
    {
      label: 'قطع اتصال',
      data: options.disconnects,
      backgroundColor: [GRAPH_COLORS.disconnect.background],
      borderColor: [GRAPH_COLORS.disconnect.point],
      ...SHARED_DATASET_CONFIG
    }
  ]
}

export const provideDisconnects = (logs: LogData[]) => {
  const result = [];
  for (let i = 0; i < logs.length; i++) {
    const log = logs[i];
    if (log.CDC_Percent > 50 || log.DC_Percent > 50) {
      result.push(disconnectValue(i, logs));
      continue;
    }
    result.push(undefined)
  }
  return result;
}

export const provideDatasetGaps = (logs: LogData[], interval: string | number, showMonth: boolean) => {
  if (logs.length > 0) {
    if (showMonth) {
      return gapForMonth(logs as any[], Number(interval)) as any[];
    }
    return gapForDay(logs as any[], Number(interval)) as any[];
  }
  return [];
}

export const extractValuesFromLogs = (logs: LogData[]) => {
  return logs.map(log => {
    if (log.isEmpty) {
      return undefined;
    }
    if (log.DC_Percent > 50 || log.CDC_Percent > 50) {
      return undefined;
    }
    return Number(log.avg);
  });
}

export const extractTimesFromLogs: ExtractTimesOverload = (logs: LogData[], format: string | boolean) => {
  const times = logs.map(log => {
    if (typeof format === 'boolean') {
      format = format ? 'D MMM - HH:mm' : 'HH:mm:ss';
    }
    return moment.from(log._id, 'en').format(format);
  });
  return times;
}

const chartOption = (unit: string): any => {
  return {
    scales: {
      yAxes: [
        {
          display: true,
          ticks: {
            callback: (value: any, _inedx: any, _values: any) => {
              return value + unit;
            }
          }
        }
      ]
    },
    tooltips: {
      enabled: true,
      mode: 'single',
      callbacks: {
        label: function (tooltipItems: any, _data: any) {
          return tooltipItems.yLabel + unit;
        }
      }
    },
  }
}

const disconnectValue = (currentIndex: number, logs: LogData[]) => {
  const current = logs[currentIndex];
  if (current.avg != null) {
    return current.avg;
  }
  for (let i = currentIndex; i >= 0; i--) {
    if (logs[i].avg != null) {
      return logs[i].avg;
    }
  }
  for (let i = currentIndex; i < logs.length; i++) {
    if (logs[i].avg != null) {
      return logs[i].avg;
    }
  }
  return 0;
}
